import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { API_BASE, setSessionToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table, TableHeader, TableHead, TableBody, TableRow, TableCell
} from "@/components/ui/table";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import EmailHealthWidget from "@/components/EmailHealthWidget";
import CampaignCard from "@/components/CampaignCard";
import PostPitchCampaignCard from "@/components/PostPitchCampaignCard";
import UnsubscribeListCard from "@/components/UnsubscribeListCard";
import EventHubAdminTab from "@/components/admin/EventHubAdminTab";
import VolunteerSignupsCard from "@/components/VolunteerSignupsCard";
import CampaignConversionsCard from "@/components/CampaignConversionsCard";

const STATUSES = ["New", "Contacted", "Pending", "Approved", "Declined", "Waitlisted",
  "Invoice Sent", "Payment Received", "Completed"];

const ALL_TAGS = [
  "Community Voter", "Pitch Viewer", "Ticket Buyer",
  "Sponsor Prospect", "Confirmed Sponsor",
  "VIP Table-Host Applicant", "Approved VIP Table Host",
  "Marketing Table Prospect", "Volunteer", "Media",
  "2027 Business Interest", "Community Partner",
];

const FORM_TYPES = [
  { value: "", label: "All forms" },
  { value: "livestream_access", label: "Livestream Access (Nightly)" },
  { value: "resource_table_host", label: "Resource Table-Host" },
  { value: "sponsorship", label: "Sponsorship" },
  { value: "marketing_table", label: "Marketing Table" },
  { value: "ticket_question", label: "Ticket Question" },
  { value: "volunteer", label: "Volunteer" },
  { value: "media", label: "Media" },
  { value: "next_year_business", label: "Next-Year Business" },
  { value: "general_question", label: "General Question" },
];

function LoginScreen() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FFFAF0] text-emerald-950 p-6">
      <div className="max-w-md w-full bg-white border border-emerald-100 rounded-2xl p-10 shadow-sm">
        <div className="text-[11px] uppercase tracking-[0.3em] text-rose-600 font-[Outfit]">Staff Only</div>
        <h1 className="font-[Cormorant_Garamond] text-4xl mt-2">Admin Dashboard</h1>
        <p className="text-slate-600 font-[Outfit] text-sm mt-3">
          Sign in with your Freedom In Me Business Showers Google account to manage submissions, contacts, judges and pitches.
        </p>
        <Button
          data-testid="login-google-btn"
          onClick={handleLogin}
          className="w-full mt-6 rounded-full bg-emerald-800 hover:bg-emerald-900 text-white h-11 font-[Outfit]"
        >
          Sign in with Google
        </Button>
      </div>
    </div>
  );
}

function StatsRow({ stats }) {
  if (!stats) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
      <Stat label="Submissions" value={stats.total_submissions} />
      <Stat label="Contacts" value={stats.total_contacts} />
      <Stat label="New" value={stats.by_status?.New || 0} />
      <Stat label="Approved" value={stats.by_status?.Approved || 0} />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-emerald-100 rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700/80 font-[Outfit]">{label}</div>
      <div className="font-[Cormorant_Garamond] text-3xl text-emerald-950 mt-1">{value}</div>
    </div>
  );
}

function SubmissionDrawer({ open, onClose, item, onChanged }) {
  const [note, setNote] = useState("");
  const [tags, setTags] = useState([]);

  useEffect(() => {
    setTags(item?.tags || []);
    setNote("");
  }, [item]);

  if (!item) return null;

  const toggleTag = (t) => setTags((cur) => cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]);

  const saveStatus = async (status) => {
    await api.patch(`/admin/submissions/${item.id}/status`, { status });
    toast.success(`Status: ${status}`);
    onChanged();
  };
  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/admin/submissions/${item.id}/notes`, { note });
    setNote("");
    toast.success("Note added");
    onChanged();
  };
  const saveTags = async () => {
    await api.patch(`/admin/submissions/${item.id}/tags`, { tags });
    toast.success("Tags updated");
    onChanged();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="submission-drawer" className="max-w-2xl bg-[#FFFAF0]">
        <DialogHeader>
          <DialogTitle className="font-[Cormorant_Garamond] text-2xl text-emerald-950">
            {item.form_label}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3 text-sm font-[Outfit]">
            <Field label="Name" value={item.name} />
            <Field label="Email" value={item.email} />
            <Field label="Phone" value={item.phone || "—"} />
            <Field label="Organization" value={item.organization || "—"} />
            <Field label="Status" value={item.status} />
            <Field label="Submitted" value={new Date(item.submitted_at).toLocaleString()} />
          </div>
          {item.message && (
            <div>
              <Label className="font-[Outfit] text-[10px] uppercase tracking-[0.2em] text-emerald-800">Message</Label>
              <div className="mt-1 bg-white border border-emerald-100 rounded-md p-3 text-sm">{item.message}</div>
            </div>
          )}
          {item.extra && Object.keys(item.extra).length > 0 && (
            <div>
              <Label className="font-[Outfit] text-[10px] uppercase tracking-[0.2em] text-emerald-800">Extra fields</Label>
              <div className="mt-1 bg-white border border-emerald-100 rounded-md p-3 text-sm">
                {Object.entries(item.extra).map(([k, v]) => (
                  <div key={k}><b>{k}:</b> {String(v)}</div>
                ))}
              </div>
            </div>
          )}
          <div>
            <Label className="font-[Outfit] text-[10px] uppercase tracking-[0.2em] text-emerald-800">Status</Label>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {STATUSES.map((s) => (
                <Button
                  key={s}
                  size="sm"
                  data-testid={`status-${s}`}
                  variant={item.status === s ? "default" : "outline"}
                  onClick={() => saveStatus(s)}
                  className={`rounded-full font-[Outfit] text-xs ${item.status === s ? "bg-emerald-800 text-white" : "border-emerald-300 text-emerald-800"}`}
                >
                  {s}
                </Button>
              ))}
            </div>
          </div>
          <div>
            <Label className="font-[Outfit] text-[10px] uppercase tracking-[0.2em] text-emerald-800">Tags</Label>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {ALL_TAGS.map((t) => (
                <button
                  key={t}
                  data-testid={`tag-${t}`}
                  onClick={() => toggleTag(t)}
                  className={`text-xs rounded-full px-3 py-1 font-[Outfit] border ${tags.includes(t) ? "bg-amber-300 border-amber-400 text-emerald-950" : "border-emerald-200 text-emerald-800 bg-white"}`}
                >{t}</button>
              ))}
            </div>
            <Button size="sm" onClick={saveTags} data-testid="save-tags" className="mt-3 rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-[Outfit]">
              Save tags
            </Button>
          </div>
          <div>
            <Label className="font-[Outfit] text-[10px] uppercase tracking-[0.2em] text-emerald-800">Internal Notes</Label>
            <div className="space-y-2 mt-2">
              {(item.notes || []).map((n, i) => (
                <div key={i} className="bg-white border border-emerald-100 rounded-md p-2 text-sm">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700/80">{n.by} · {new Date(n.at).toLocaleString()}</div>
                  <div>{n.text}</div>
                </div>
              ))}
              <Textarea data-testid="note-input" rows={2} placeholder="Add a note…" value={note} onChange={(e) => setNote(e.target.value)} className="bg-white border-emerald-200" />
              <Button size="sm" onClick={addNote} data-testid="add-note" className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-[Outfit]">Add note</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700/80">{label}</div>
      <div className="text-emerald-950 mt-0.5">{value}</div>
    </div>
  );
}

function SubmissionsTab() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [formType, setFormType] = useState("");
  const [status, setStatus] = useState("");
  const [tag, setTag] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const params = {};
    if (formType) params.form_type = formType;
    if (status) params.status = status;
    if (tag) params.tag = tag;
    if (q) params.q = q;
    const [list, st] = await Promise.all([
      api.get("/admin/submissions", { params }),
      api.get("/admin/stats"),
    ]);
    setItems(list.data.items);
    setStats(st.data);
  };

  useEffect(() => { load(); }, [formType, status, tag, q]);

  return (
    <div>
      <EmailHealthWidget />
      <VolunteerSignupsCard />
      <CampaignConversionsCard />
      <PostPitchCampaignCard />
      <CampaignCard />
      <UnsubscribeListCard />
      <StatsRow stats={stats} />
      <div className="bg-white border border-emerald-100 rounded-xl p-4">
        <div className="flex flex-wrap gap-2 items-center">
          <Input data-testid="search-input" placeholder="Search name, email, org…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs bg-white border-emerald-200 focus-visible:ring-emerald-500/50" />
          <Select value={formType || "_all"} onValueChange={(v) => setFormType(v === "_all" ? "" : v)}>
            <SelectTrigger data-testid="filter-form-type" className="w-48 bg-white border-emerald-200"><SelectValue placeholder="Form type" /></SelectTrigger>
            <SelectContent>
              {FORM_TYPES.map((f) => (
                <SelectItem key={f.value || "_all"} value={f.value || "_all"}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={status || "_all"} onValueChange={(v) => setStatus(v === "_all" ? "" : v)}>
            <SelectTrigger data-testid="filter-status" className="w-40 bg-white border-emerald-200"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={tag || "_all"} onValueChange={(v) => setTag(v === "_all" ? "" : v)}>
            <SelectTrigger data-testid="filter-tag" className="w-48 bg-white border-emerald-200"><SelectValue placeholder="Tag" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All tags</SelectItem>
              {ALL_TAGS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button
              data-testid="trigger-replay-digest"
              variant="outline"
              onClick={async () => {
                const day = window.prompt(
                  "Send replay digest for which pitch day?\n\n• Leave EMPTY for normal cron behavior (only unsent + due)\n• Enter 2026-06-16 to FORCE re-send to all June 16 viewers\n• Enter 2026-06-17 to FORCE re-send to all June 17 viewers\n• Enter 2026-06-18 to FORCE re-send to all June 18 viewers\n\nWARNING: Force mode ignores idempotency and re-sends to everyone for that day. Use only for outage recovery.",
                  ""
                );
                if (day === null) return; // cancelled
                const forceLabel = day ? `\n\n⚠️ FORCE re-send for ${day}` : "";
                if (!window.confirm(`Send the replay digest now?${forceLabel}\n\nResend has a 5/sec rate limit, so 50 emails ≈ 13 sec.`)) return;
                try {
                  const url = day ? `/admin/livestream-replays/run?force_pitch_day=${encodeURIComponent(day)}` : "/admin/livestream-replays/run";
                  const r = await api.post(url);
                  alert(`✅ Replay digest job complete\n\nBackfilled records: ${r.data.backfilled ?? 0}\nEmails sent: ${r.data.sent ?? 0}\nFailed: ${r.data.failed ?? 0}`);
                } catch (e) {
                  alert(`❌ Replay digest failed: ${e?.response?.data?.detail || e.message}`);
                }
              }}
              className="rounded-full border-rose-300 text-rose-700 hover:bg-rose-50 font-[Outfit]"
            >
              Send Replay Digests
            </Button>
            <Button
              data-testid="trigger-followups"
              variant="outline"
              onClick={async () => {
                const day = window.prompt(
                  "Send follow-up for which pitch day?\n\n• Leave EMPTY for normal cron behavior\n• Enter 2026-06-16 to FORCE re-send to all June 16 viewers\n• Enter 2026-06-17 to FORCE re-send to all June 17 viewers\n• Enter 2026-06-18 to FORCE re-send to all June 18 viewers",
                  ""
                );
                if (day === null) return;
                const forceLabel = day ? `\n\n⚠️ FORCE re-send for ${day}` : "";
                if (!window.confirm(`Send follow-up emails now?${forceLabel}`)) return;
                try {
                  const url = day ? `/admin/livestream-followups/run?force_pitch_day=${encodeURIComponent(day)}` : "/admin/livestream-followups/run";
                  const r = await api.post(url);
                  alert(`✅ Follow-up job complete\n\nBackfilled records: ${r.data.backfilled ?? 0}\nEmails sent: ${r.data.sent ?? 0}\nFailed: ${r.data.failed ?? 0}`);
                } catch (e) {
                  alert(`❌ Follow-up send failed: ${e?.response?.data?.detail || e.message}`);
                }
              }}
              className="rounded-full border-emerald-300 text-emerald-800 hover:bg-emerald-50 font-[Outfit]"
            >
              Send Follow-ups
            </Button>
            <Button
              data-testid="trigger-volunteer-roster"
              variant="outline"
              onClick={async () => {
                const to = window.prompt(
                  "Send volunteer roster digest to which email?\n\n• Leave EMPTY to use the configured recipient (Sadie Singleton)\n• Or type any email address to send a one-time preview",
                  ""
                );
                if (to === null) return;
                if (!window.confirm(`Send volunteer roster digest now?${to ? ` (to ${to})` : ' (to Sadie)'}\n\nIncludes everyone who signed up in the last 14 days. CSV will be attached.`)) return;
                try {
                  const url = to ? `/admin/volunteer-roster/test?to=${encodeURIComponent(to)}` : "/admin/volunteer-roster/test";
                  const r = await api.post(url);
                  alert(`✅ Volunteer roster digest sent\n\nRecipient: ${r.data.recipient}\nVolunteers in roster: ${r.data.count}\nCSV attached: ${r.data.filename}`);
                } catch (e) {
                  alert(`❌ Send failed: ${e?.response?.data?.detail || e.message}`);
                }
              }}
              className="rounded-full border-amber-300 text-amber-800 hover:bg-amber-50 font-[Outfit]"
            >
              Send Volunteer Roster
            </Button>
            <Button data-testid="export-csv" variant="outline" onClick={async () => {
              const r = await api.get(`/admin/submissions.csv${formType ? `?form_type=${formType}` : ""}`, { responseType: "blob" });
              const url = URL.createObjectURL(r.data);
              const a = document.createElement("a"); a.href = url; a.download = "submissions.csv";
              document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
            }} className="rounded-full border-emerald-300 text-emerald-800 hover:bg-emerald-50 font-[Outfit]">Export CSV</Button>
          </div>
        </div>
        <div className="mt-4 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Form</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id} data-testid={`sub-row-${it.id}`}>
                  <TableCell className="text-xs font-[Outfit]">{it.form_label}</TableCell>
                  <TableCell className="font-[Outfit]">{it.name}</TableCell>
                  <TableCell className="text-xs font-[Outfit]">{it.email}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(it.tags || []).map((t) => <Badge key={t} className="bg-amber-200 text-emerald-950 font-[Outfit]">{t}</Badge>)}
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="border-emerald-300 text-emerald-800 font-[Outfit]">{it.status}</Badge></TableCell>
                  <TableCell className="text-xs font-[Outfit]">{new Date(it.submitted_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Button data-testid={`open-sub-${it.id}`} size="sm" onClick={() => setSelected(it)} className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-[Outfit]">Open</Button>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500 font-[Outfit]">No submissions yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <SubmissionDrawer
        open={!!selected}
        onClose={() => setSelected(null)}
        item={selected}
        onChanged={async () => { await load(); if (selected) { const r = await api.get(`/admin/submissions/${selected.id}`); setSelected(r.data); } }}
      />
    </div>
  );
}

function ContactsTab() {
  const [items, setItems] = useState([]);
  const [tag, setTag] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    const params = {};
    if (tag) params.tag = tag;
    if (q) params.q = q;
    const r = await api.get("/admin/contacts", { params });
    setItems(r.data.items);
  };
  useEffect(() => { load(); }, [tag, q]);

  return (
    <div className="bg-white border border-emerald-100 rounded-xl p-4">
      <div className="flex flex-wrap gap-2 items-center">
        <Input data-testid="contact-search" placeholder="Search contacts…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs bg-white border-emerald-200" />
        <Select value={tag || "_all"} onValueChange={(v) => setTag(v === "_all" ? "" : v)}>
          <SelectTrigger className="w-48 bg-white border-emerald-200" data-testid="contact-filter-tag"><SelectValue placeholder="Tag" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All tags</SelectItem>
            {ALL_TAGS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto">
          <Button variant="outline" data-testid="contacts-export" onClick={async () => {
            const r = await api.get("/admin/contacts.csv", { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a"); a.href = url; a.download = "contacts.csv";
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
          }} className="rounded-full border-emerald-300 text-emerald-800 hover:bg-emerald-50 font-[Outfit]">Export CSV</Button>
        </div>
      </div>
      <div className="mt-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead>
            <TableHead>Tags</TableHead><TableHead>Updated</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c.contact_id}>
                <TableCell className="font-[Outfit]">{c.name || "—"}</TableCell>
                <TableCell className="text-xs font-[Outfit]">{c.email}</TableCell>
                <TableCell className="text-xs font-[Outfit]">{c.phone || "—"}</TableCell>
                <TableCell><div className="flex flex-wrap gap-1">{(c.tags || []).map((t) => <Badge key={t} className="bg-amber-200 text-emerald-950">{t}</Badge>)}</div></TableCell>
                <TableCell className="text-xs font-[Outfit]">{new Date(c.updated_at).toLocaleDateString()}</TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500 font-[Outfit]">No contacts yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ContentTab() {
  const [days, setDays] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [judges, setJudges] = useState([]);
  const [editingB, setEditingB] = useState(null);
  const [editingJ, setEditingJ] = useState(null);
  const [siteContent, setSiteContent] = useState(null);

  const load = async () => {
    const [s, sc] = await Promise.all([
      api.get("/pitch-schedule?include_tentative=true"),
      api.get("/site-content"),
    ]);
    setDays(s.data.days);
    setBusinesses(s.data.businesses);
    setJudges(s.data.judges);
    setSiteContent(sc.data);
  };
  useEffect(() => { load(); }, []);

  const saveSiteContent = async (patch) => {
    await api.put("/admin/site-content", patch);
    toast.success("Saved");
    await load();
  };

  const savePitchDay = async (d) => {
    await api.put(`/admin/pitch-days/${d.date}`, {
      zoom_url: d.zoom_url || "",
      start_time: d.start_time || "7:00 PM ET",
      note: d.note || "",
    });
    toast.success(`Updated ${d.date}`);
    await load();
  };

  const saveBusiness = async () => {
    await api.post("/admin/businesses", editingB);
    toast.success("Business saved");
    setEditingB(null);
    await load();
  };
  const deleteBusiness = async (id) => {
    if (!window.confirm("Delete this business?")) return;
    await api.delete(`/admin/businesses/${id}`);
    await load();
  };
  const saveJudge = async () => {
    await api.post("/admin/judges", editingJ);
    toast.success("Judge saved");
    setEditingJ(null);
    await load();
  };
  const deleteJudge = async (id) => {
    if (!window.confirm("Delete this judge?")) return;
    await api.delete(`/admin/judges/${id}`);
    await load();
  };

  return (
    <div className="space-y-8">
      {/* Campaign control */}
      <CampaignSettingsPanel />

      {/* Banners & Hero Images */}
      {siteContent && (
        <BannersEditor siteContent={siteContent} onSave={saveSiteContent} />
      )}

      {/* Sponsors */}
      <SponsorsManager />

      {/* Pitch Days */}
      <section className="bg-white border border-emerald-100 rounded-xl p-5">
        <h3 className="font-[Cormorant_Garamond] text-2xl text-emerald-950">Pitch Days · Zoom links</h3>
        <p className="text-sm text-slate-600 font-[Outfit]">Add the Zoom URL for each pitch night when ready.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          {days.map((d, idx) => (
            <div key={d.date} className="border border-emerald-100 rounded-lg p-4 bg-[#FFFAF0]">
              <div className="font-[Cormorant_Garamond] text-xl">{d.date}</div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-emerald-800 font-[Outfit]">Zoom URL</Label>
              <Input data-testid={`zoom-${d.date}`} value={d.zoom_url || ""} onChange={(e) => { const nd = [...days]; nd[idx].zoom_url = e.target.value; setDays(nd); }} className="bg-white border-emerald-200 mt-1" placeholder="https://us02web.zoom.us/…" />
              <Label className="text-[10px] uppercase tracking-[0.2em] text-emerald-800 font-[Outfit] mt-3 block">Start time</Label>
              <Input value={d.start_time || ""} onChange={(e) => { const nd = [...days]; nd[idx].start_time = e.target.value; setDays(nd); }} className="bg-white border-emerald-200 mt-1" />
              <Label className="text-[10px] uppercase tracking-[0.2em] text-emerald-800 font-[Outfit] mt-3 block">Note</Label>
              <Textarea rows={2} value={d.note || ""} onChange={(e) => { const nd = [...days]; nd[idx].note = e.target.value; setDays(nd); }} className="bg-white border-emerald-200 mt-1" />
              <Button onClick={() => savePitchDay(days[idx])} data-testid={`save-day-${d.date}`} size="sm" className="mt-3 rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-[Outfit]">Save</Button>
            </div>
          ))}
        </div>
      </section>

      {/* Businesses */}
      <section className="bg-white border border-emerald-200 rounded-lg p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-[Cormorant_Garamond] text-2xl text-emerald-950">Honoree Interview &amp; Pitches Schedule</h3>
          <div className="flex gap-2">
            <Button
              data-testid="bulk-import-honorees"
              variant="outline"
              onClick={async () => {
                if (!window.confirm("This will WIPE the current honoree list and reseed the official 14-honoree 2026 schedule (with photos, times, Zoom link, and Tentative flags). Continue?")) return;
                try {
                  const r = await api.post("/admin/bulk-import-honorees");
                  toast.success(`Imported ${r.data.count} honorees`);
                  await load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Bulk import failed");
                }
              }}
              className="rounded-full border-rose-400 text-rose-700 hover:bg-rose-50 font-[Outfit] font-semibold"
            >
              ↻ Bulk import 2026 schedule
            </Button>
            <Button data-testid="add-business" onClick={() => setEditingB({ name: "", business_name: "", credential: "", bio: "", owner: "", industry: "", category: "Honoree Interview & Pitches", description: "", photo_url: "", logo_url: "", video_url: "", replay_link: "", pitch_day: "2026-06-16", interview_time: "", start_time: "", end_time: "", timezone: "America/New_York", livestream_url: "https://us02web.zoom.us/j/4973874078?pwd=emZkMHlvR1YvbUF2d3FQNWZQblhwdz09", vote_url: "", status_override: "", schedule_status: "Confirmed" })} className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-[Outfit]">+ Add Honoree</Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {businesses.map((b) => (
            <div key={b.id} className="border border-emerald-200 rounded-lg p-4 bg-white">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-rose-600 font-[Outfit] font-bold">
                    {b.pitch_day} {b.interview_time && `· ${b.interview_time}`}
                  </div>
                  <div className="font-[Cormorant_Garamond] text-xl text-emerald-950 mt-0.5">{b.name}</div>
                  {b.business_name && <div className="text-xs text-emerald-800 font-[Outfit] font-semibold">{b.business_name}</div>}
                </div>
                {b.schedule_status && b.schedule_status !== "Confirmed" && (
                  <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border bg-amber-200 text-emerald-950 border-amber-400 font-[Outfit] font-bold">{b.schedule_status}</span>
                )}
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" data-testid={`edit-biz-${b.id}`} onClick={() => setEditingB(b)} className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-[Outfit]">Edit</Button>
                <Button size="sm" variant="outline" onClick={() => deleteBusiness(b.id)} className="rounded-full border-rose-300 text-rose-700 font-[Outfit]">Delete</Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Judges */}
      <section className="bg-white border border-emerald-200 rounded-lg p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-[Cormorant_Garamond] text-2xl text-emerald-950">Judges</h3>
          <div className="flex gap-2">
            <Button
              data-testid="bulk-import-judges"
              variant="outline"
              onClick={async () => {
                if (!window.confirm("This will WIPE the current judges list and reseed the official 2026 judges. Continue?")) return;
                try {
                  const r = await api.post("/admin/bulk-import-judges");
                  toast.success(`Imported ${r.data.count} judges`);
                  await load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Bulk import failed");
                }
              }}
              className="rounded-full border-rose-400 text-rose-700 hover:bg-rose-50 font-[Outfit] font-semibold"
            >
              ↻ Bulk import 2026 judges
            </Button>
            <Button data-testid="add-judge" onClick={() => setEditingJ({ name: "", title: "", company: "", bio: "", photo_url: "" })} className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-[Outfit]">+ Add</Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {judges.map((j) => (
            <div key={j.id} className="border border-emerald-100 rounded-lg p-4 bg-[#FFFAF0]">
              <div className="font-[Cormorant_Garamond] text-xl">{j.name}</div>
              <div className="text-xs text-emerald-800 font-[Outfit]">{j.title} · {j.company}</div>
              <p className="text-sm text-slate-600 font-[Outfit] mt-2 line-clamp-3">{j.bio}</p>
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={() => setEditingJ(j)} className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-[Outfit]">Edit</Button>
                <Button size="sm" variant="outline" onClick={() => deleteJudge(j.id)} className="rounded-full border-rose-300 text-rose-700 font-[Outfit]">Delete</Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Business editor */}
      <Dialog open={!!editingB} onOpenChange={(v) => !v && setEditingB(null)}>
        <DialogContent className="max-w-lg bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-[Cormorant_Garamond] text-2xl text-emerald-950">Edit Business</DialogTitle></DialogHeader>
          {editingB && (
            <div className="space-y-3">
              <Input data-testid="biz-name" placeholder="Honoree full name (display)" value={editingB.name || ""} onChange={(e) => setEditingB({ ...editingB, name: e.target.value })} className="bg-white border-emerald-200" />
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Business / brand name" value={editingB.business_name || ""} onChange={(e) => setEditingB({ ...editingB, business_name: e.target.value })} className="bg-white border-emerald-200" />
                <Input placeholder="Credential (e.g., PhD., ACUE)" value={editingB.credential || ""} onChange={(e) => setEditingB({ ...editingB, credential: e.target.value })} className="bg-white border-emerald-200" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Owner (if different from honoree)" value={editingB.owner || ""} onChange={(e) => setEditingB({ ...editingB, owner: e.target.value })} className="bg-white border-emerald-200" />
                <Input placeholder="Category" value={editingB.category || ""} onChange={(e) => setEditingB({ ...editingB, category: e.target.value })} className="bg-white border-emerald-200" />
              </div>
              <Textarea placeholder="Bio (public)" rows={3} value={editingB.bio || ""} onChange={(e) => setEditingB({ ...editingB, bio: e.target.value })} className="bg-white border-emerald-200" />
              <Input placeholder="Description (fallback if no bio)" value={editingB.description || ""} onChange={(e) => setEditingB({ ...editingB, description: e.target.value })} className="bg-white border-emerald-200" />
              <Input placeholder="Photo URL (headshot)" value={editingB.photo_url || ""} onChange={(e) => setEditingB({ ...editingB, photo_url: e.target.value })} className="bg-white border-emerald-200" />
              <Input placeholder="Logo URL" value={editingB.logo_url || ""} onChange={(e) => setEditingB({ ...editingB, logo_url: e.target.value })} className="bg-white border-emerald-200" />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.15em] text-emerald-800 font-[Outfit]">Pitch day</Label>
                  <Select value={editingB.pitch_day} onValueChange={(v) => setEditingB({ ...editingB, pitch_day: v })}>
                    <SelectTrigger className="bg-white border-emerald-200 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2026-06-16">Tuesday, June 16</SelectItem>
                      <SelectItem value="2026-06-17">Wednesday, June 17</SelectItem>
                      <SelectItem value="2026-06-18">Thursday, June 18</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.15em] text-emerald-800 font-[Outfit]">Interview time (display)</Label>
                  <Input data-testid="biz-itime" placeholder="5:30 PM" value={editingB.interview_time || ""} onChange={(e) => setEditingB({ ...editingB, interview_time: e.target.value })} className="bg-white border-emerald-200 mt-1" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.15em] text-emerald-800 font-[Outfit]">Start (ISO with TZ)</Label>
                  <Input data-testid="biz-start" placeholder="2026-06-16T17:30:00-04:00" value={editingB.start_time || ""} onChange={(e) => setEditingB({ ...editingB, start_time: e.target.value })} className="bg-white border-emerald-200 mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.15em] text-emerald-800 font-[Outfit]">End (ISO with TZ)</Label>
                  <Input data-testid="biz-end" placeholder="2026-06-16T18:00:00-04:00" value={editingB.end_time || ""} onChange={(e) => setEditingB({ ...editingB, end_time: e.target.value })} className="bg-white border-emerald-200 mt-1" />
                </div>
              </div>

              <Input placeholder="Livestream URL (Zoom)" value={editingB.livestream_url || ""} onChange={(e) => setEditingB({ ...editingB, livestream_url: e.target.value })} className="bg-white border-emerald-200" />
              <Input placeholder="Replay link (YouTube/video URL)" value={editingB.replay_link || editingB.video_url || ""} onChange={(e) => setEditingB({ ...editingB, replay_link: e.target.value })} className="bg-white border-emerald-200" />
              <Input placeholder="Custom vote URL (optional)" value={editingB.vote_url || ""} onChange={(e) => setEditingB({ ...editingB, vote_url: e.target.value })} className="bg-white border-emerald-200" />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.15em] text-emerald-800 font-[Outfit]">Schedule status (public/private)</Label>
                  <Select value={editingB.schedule_status || "Confirmed"} onValueChange={(v) => setEditingB({ ...editingB, schedule_status: v })}>
                    <SelectTrigger data-testid="biz-sched-status" className="bg-white border-emerald-200 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Confirmed">Confirmed (public)</SelectItem>
                      <SelectItem value="Tentative">Tentative (admin only)</SelectItem>
                      <SelectItem value="Awaiting Information">Awaiting Information</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.15em] text-emerald-800 font-[Outfit]">Live status override</Label>
                  <Select value={editingB.status_override || "_auto"} onValueChange={(v) => setEditingB({ ...editingB, status_override: v === "_auto" ? "" : v })}>
                    <SelectTrigger data-testid="biz-status" className="bg-white border-emerald-200 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_auto">Auto (from times)</SelectItem>
                      <SelectItem value="Upcoming">Upcoming</SelectItem>
                      <SelectItem value="Live Now">Live Now</SelectItem>
                      <SelectItem value="Pitch Complete">Pitch Complete</SelectItem>
                      <SelectItem value="Replay Available">Replay Available</SelectItem>
                      <SelectItem value="Voting Closed">Voting Closed</SelectItem>
                      <SelectItem value="Cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button data-testid="save-biz" onClick={saveBusiness} className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-[Outfit] font-semibold">Save</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Judge editor */}
      <Dialog open={!!editingJ} onOpenChange={(v) => !v && setEditingJ(null)}>
        <DialogContent className="max-w-lg bg-[#FFFAF0]">
          <DialogHeader><DialogTitle className="font-[Cormorant_Garamond] text-2xl text-emerald-950">Edit Judge</DialogTitle></DialogHeader>
          {editingJ && (
            <div className="space-y-3">
              <Input placeholder="Name" value={editingJ.name} onChange={(e) => setEditingJ({ ...editingJ, name: e.target.value })} className="bg-white border-emerald-200" />
              <Input placeholder="Title" value={editingJ.title} onChange={(e) => setEditingJ({ ...editingJ, title: e.target.value })} className="bg-white border-emerald-200" />
              <Input placeholder="Company" value={editingJ.company} onChange={(e) => setEditingJ({ ...editingJ, company: e.target.value })} className="bg-white border-emerald-200" />
              <Textarea placeholder="Bio" rows={3} value={editingJ.bio} onChange={(e) => setEditingJ({ ...editingJ, bio: e.target.value })} className="bg-white border-emerald-200" />
              <Input placeholder="Photo URL" value={editingJ.photo_url} onChange={(e) => setEditingJ({ ...editingJ, photo_url: e.target.value })} className="bg-white border-emerald-200" />
              <Button onClick={saveJudge} data-testid="save-judge" className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-[Outfit]">Save</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CampaignSettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/campaign-settings");
    setSettings(r.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await api.put("/admin/campaign-settings", {
        auto_phase: settings.auto_phase,
        phase_override: settings.auto_phase ? null : Number(settings.phase_override || 1),
        phase2_start: settings.phase2_start,
        phase2_end: settings.phase2_end,
        voting_close: settings.voting_close,
        phase4_end: settings.phase4_end,
        section_visibility: settings.section_visibility,
        hero_overrides: settings.hero_overrides || {},
      });
      toast.success("Campaign settings saved");
      await load();
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;

  const toggleSection = (key) => {
    setSettings({
      ...settings,
      section_visibility: { ...settings.section_visibility, [key]: !settings.section_visibility[key] },
    });
  };

  const phaseLabels = {
    1: "1 · Before Pitches",
    2: "2 · Live Pitches",
    3: "3 · Final Voting",
    4: "4 · After Voting Closes",
    5: "5 · After Event (Highlights)",
  };

  return (
    <section className="bg-white border border-emerald-200 rounded-lg p-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-[Cormorant_Garamond] text-2xl text-emerald-950">Campaign Phase Control</h3>
          <p className="text-sm text-slate-600 font-[Outfit]">Set automatic switching or manually pin the current phase. Current computed phase: <b>{settings.current_phase}</b></p>
        </div>
        <Button data-testid="save-campaign-settings" disabled={saving} onClick={save} className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-[Outfit] font-semibold">Save</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5">
        <div className="space-y-3">
          <label className="flex items-center gap-3 text-sm font-[Outfit]">
            <input
              type="checkbox"
              data-testid="auto-phase-toggle"
              checked={!!settings.auto_phase}
              onChange={(e) => setSettings({ ...settings, auto_phase: e.target.checked })}
            />
            <span><b>Auto phase switching</b> (recommended). When off, you can pin a phase below.</span>
          </label>
          {!settings.auto_phase && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] text-emerald-800 font-[Outfit]">Pinned Phase</Label>
              <Select value={String(settings.phase_override || 1)} onValueChange={(v) => setSettings({ ...settings, phase_override: Number(v) })}>
                <SelectTrigger data-testid="pinned-phase" className="bg-white border-emerald-200 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1,2,3,4,5].map((p) => <SelectItem key={p} value={String(p)}>{phaseLabels[p]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-emerald-800 font-[Outfit]">Pitch start (ISO with timezone)</Label>
            <Input value={settings.phase2_start} onChange={(e) => setSettings({ ...settings, phase2_start: e.target.value })} className="bg-white border-emerald-200 mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-emerald-800 font-[Outfit]">Pitch end</Label>
            <Input value={settings.phase2_end} onChange={(e) => setSettings({ ...settings, phase2_end: e.target.value })} className="bg-white border-emerald-200 mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-emerald-800 font-[Outfit]">Voting close</Label>
            <Input data-testid="voting-close-input" value={settings.voting_close} onChange={(e) => setSettings({ ...settings, voting_close: e.target.value })} className="bg-white border-emerald-200 mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-emerald-800 font-[Outfit]">Main event end</Label>
            <Input value={settings.phase4_end} onChange={(e) => setSettings({ ...settings, phase4_end: e.target.value })} className="bg-white border-emerald-200 mt-1" />
          </div>
        </div>
      </div>

      <div className="mt-6 border-t border-emerald-100 pt-5">
        <div className="text-[10px] uppercase tracking-[0.15em] text-emerald-800 font-[Outfit] font-bold mb-3">Section visibility</div>
        <div className="flex flex-wrap gap-2">
          {Object.keys(settings.section_visibility || {}).map((k) => (
            <button
              key={k}
              data-testid={`toggle-section-${k}`}
              onClick={() => toggleSection(k)}
              className={`text-xs px-3 py-1.5 rounded-full font-[Outfit] border ${settings.section_visibility[k] ? "bg-emerald-800 text-white border-emerald-800" : "bg-white text-emerald-800 border-emerald-300"}`}
            >
              {settings.section_visibility[k] ? "✓ " : "○ "}{k}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function SponsorsManager() {
  const [sponsors, setSponsors] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const r = await api.get("/sponsors");
    setSponsors(r.data.items || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    await api.post("/admin/sponsors", editing);
    toast.success("Sponsor saved");
    setEditing(null);
    await load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete sponsor?")) return;
    await api.delete(`/admin/sponsors/${id}`);
    await load();
  };

  return (
    <section className="bg-white border border-emerald-100 rounded-xl p-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-[Cormorant_Garamond] text-2xl text-emerald-950">Sponsors (scrolling strip)</h3>
          <p className="text-sm text-slate-600 font-[Outfit]">Logos appear in the auto-scrolling strip on the homepage. Featured sponsors show first.</p>
        </div>
        <Button data-testid="add-sponsor" onClick={() => setEditing({ name: "", tier: "Community Partner", logo_url: "", website: "", featured: false })} className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-[Outfit]">+ Add Sponsor</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        {sponsors.map((s) => (
          <div key={s.id} className="border border-emerald-100 rounded-lg p-4 bg-[#FFFAF0]">
            <div className="flex items-center justify-between gap-2">
              <div className="font-[Cormorant_Garamond] text-xl text-emerald-950">{s.name}</div>
              {s.featured && <Badge className="bg-amber-300 text-emerald-950">Featured</Badge>}
            </div>
            <div className="text-xs text-emerald-800 font-[Outfit] mt-1">{s.tier}</div>
            {s.logo_url && <img src={s.logo_url} alt={s.name} className="h-10 mt-3 object-contain" />}
            <div className="flex gap-2 mt-3">
              <Button size="sm" data-testid={`edit-sponsor-${s.id}`} onClick={() => setEditing(s)} className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-[Outfit]">Edit</Button>
              <Button size="sm" variant="outline" onClick={() => del(s.id)} className="rounded-full border-rose-300 text-rose-700 font-[Outfit]">Delete</Button>
            </div>
          </div>
        ))}
        {sponsors.length === 0 && (
          <div className="col-span-full text-center py-6 text-slate-500 font-[Outfit] text-sm">No sponsors yet.</div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-md bg-[#FFFAF0]">
          <DialogHeader><DialogTitle className="font-[Cormorant_Garamond] text-2xl text-emerald-950">Sponsor</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Input data-testid="sponsor-name" placeholder="Name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="bg-white border-emerald-200" />
              <Input placeholder="Tier (Presenting / Platinum / Gold / Silver / Community Partner)" value={editing.tier} onChange={(e) => setEditing({ ...editing, tier: e.target.value })} className="bg-white border-emerald-200" />
              <Input placeholder="Logo URL" value={editing.logo_url} onChange={(e) => setEditing({ ...editing, logo_url: e.target.value })} className="bg-white border-emerald-200" />
              <Input placeholder="Website" value={editing.website} onChange={(e) => setEditing({ ...editing, website: e.target.value })} className="bg-white border-emerald-200" />
              <label className="flex items-center gap-2 text-sm font-[Outfit] text-emerald-900">
                <input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} />
                Featured (shown first in the strip)
              </label>
              <Button data-testid="save-sponsor" onClick={save} className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-[Outfit]">Save</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function BannersEditor({ siteContent, onSave }) {
  const [hero, setHero] = useState({ image_url: siteContent?.hero?.image_url || "" });
  const [vip, setVip] = useState({ ...(siteContent?.vip_banner || {}) });
  const [mainB, setMainB] = useState({ ...(siteContent?.main_banner || {}) });
  const [voteImg, setVoteImg] = useState(siteContent?.vote_section_image || "");
  const [sponsorImg, setSponsorImg] = useState(siteContent?.sponsor_section_image || "");
  const [cohortImg, setCohortImg] = useState(siteContent?.cohort_image || "");

  useEffect(() => {
    setHero({ image_url: siteContent?.hero?.image_url || "" });
    setVip({ ...(siteContent?.vip_banner || {}) });
    setMainB({ ...(siteContent?.main_banner || {}) });
    setVoteImg(siteContent?.vote_section_image || "");
    setSponsorImg(siteContent?.sponsor_section_image || "");
    setCohortImg(siteContent?.cohort_image || "");
  }, [siteContent]);

  const saveAll = () => onSave({
    hero,
    vip_banner: vip,
    main_banner: mainB,
    vote_section_image: voteImg,
    sponsor_section_image: sponsorImg,
    cohort_image: cohortImg,
  });

  const fieldSet = (banner, setBanner, label, testid) => (
    <div className="bg-[#FFFAF0] border border-emerald-100 rounded-lg p-4 space-y-3">
      <div className="text-[10px] uppercase tracking-[0.25em] text-rose-600 font-[Outfit]">{label}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-emerald-800 font-[Outfit]">Badge</Label>
          <Input data-testid={`${testid}-badge`} value={banner.badge_text || ""} onChange={(e) => setBanner({ ...banner, badge_text: e.target.value })} className="bg-white border-emerald-200 mt-1" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-emerald-800 font-[Outfit]">Date text</Label>
          <Input data-testid={`${testid}-date`} value={banner.date_text || ""} onChange={(e) => setBanner({ ...banner, date_text: e.target.value })} className="bg-white border-emerald-200 mt-1" />
        </div>
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.15em] text-emerald-800 font-[Outfit]">Title</Label>
        <Input data-testid={`${testid}-title`} value={banner.title || ""} onChange={(e) => setBanner({ ...banner, title: e.target.value })} className="bg-white border-emerald-200 mt-1" />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.15em] text-emerald-800 font-[Outfit]">Description</Label>
        <Textarea rows={3} data-testid={`${testid}-desc`} value={banner.description || ""} onChange={(e) => setBanner({ ...banner, description: e.target.value })} className="bg-white border-emerald-200 mt-1" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-emerald-800 font-[Outfit]">Primary button label</Label>
          <Input data-testid={`${testid}-p-label`} value={banner.primary_btn_label || ""} onChange={(e) => setBanner({ ...banner, primary_btn_label: e.target.value })} className="bg-white border-emerald-200 mt-1" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-emerald-800 font-[Outfit]">Primary button URL (or #forms)</Label>
          <Input data-testid={`${testid}-p-url`} value={banner.primary_btn_url || ""} onChange={(e) => setBanner({ ...banner, primary_btn_url: e.target.value })} className="bg-white border-emerald-200 mt-1" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-emerald-800 font-[Outfit]">Secondary button label</Label>
          <Input data-testid={`${testid}-s-label`} value={banner.secondary_btn_label || ""} onChange={(e) => setBanner({ ...banner, secondary_btn_label: e.target.value })} className="bg-white border-emerald-200 mt-1" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-emerald-800 font-[Outfit]">Secondary button URL (or #forms)</Label>
          <Input data-testid={`${testid}-s-url`} value={banner.secondary_btn_url || ""} onChange={(e) => setBanner({ ...banner, secondary_btn_url: e.target.value })} className="bg-white border-emerald-200 mt-1" />
        </div>
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.15em] text-emerald-800 font-[Outfit]">Image URL</Label>
        <div className="flex gap-2 items-start mt-1">
          <Input data-testid={`${testid}-image`} value={banner.image_url || ""} onChange={(e) => setBanner({ ...banner, image_url: e.target.value })} className="bg-white border-emerald-200" placeholder="https://… (paste from your hosting or Business Showers site)" />
          {banner.image_url && (
            <img src={banner.image_url} alt="" className="h-12 w-20 object-cover rounded border border-emerald-100" />
          )}
        </div>
      </div>
    </div>
  );

  return (
    <section className="bg-white border border-emerald-100 rounded-xl p-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-[Cormorant_Garamond] text-2xl text-emerald-950">Banners & Images</h3>
          <p className="text-sm text-slate-600 font-[Outfit]">Edit the hero, VIP banner, Main Event banner, and section images shown on the public site.</p>
        </div>
        <Button data-testid="save-banners" onClick={saveAll} className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-[Outfit]">Save all</Button>
      </div>

      <div className="grid grid-cols-1 gap-4 mt-4">
        <div className="bg-[#FFFAF0] border border-emerald-100 rounded-lg p-4 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-rose-600 font-[Outfit]">Hero background image</div>
          <div className="flex gap-2 items-start">
            <Input data-testid="hero-image-url" value={hero.image_url} onChange={(e) => setHero({ image_url: e.target.value })} className="bg-white border-emerald-200" placeholder="https://…" />
            {hero.image_url && <img src={hero.image_url} alt="" className="h-16 w-28 object-cover rounded border border-emerald-100" />}
          </div>
        </div>

        {fieldSet(vip, setVip, "VIP Experience banner · July 17", "vip")}
        {fieldSet(mainB, setMainB, "Main Celebration banner · July 18", "main")}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-[#FFFAF0] border border-emerald-100 rounded-lg p-4 space-y-2">
            <div className="text-[10px] uppercase tracking-[0.25em] text-rose-600 font-[Outfit]">Community Vote section image</div>
            <div className="flex gap-2 items-start">
              <Input data-testid="vote-image" value={voteImg} onChange={(e) => setVoteImg(e.target.value)} className="bg-white border-emerald-200" />
              {voteImg && <img src={voteImg} alt="" className="h-12 w-20 object-cover rounded border border-emerald-100" />}
            </div>
          </div>
          <div className="bg-[#FFFAF0] border border-emerald-100 rounded-lg p-4 space-y-2">
            <div className="text-[10px] uppercase tracking-[0.25em] text-rose-600 font-[Outfit]">Sponsor / Support section image</div>
            <div className="flex gap-2 items-start">
              <Input data-testid="sponsor-image" value={sponsorImg} onChange={(e) => setSponsorImg(e.target.value)} className="bg-white border-emerald-200" />
              {sponsorImg && <img src={sponsorImg} alt="" className="h-12 w-20 object-cover rounded border border-emerald-100" />}
            </div>
          </div>
          <div className="bg-[#FFFAF0] border border-emerald-100 rounded-lg p-4 space-y-2">
            <div className="text-[10px] uppercase tracking-[0.25em] text-rose-600 font-[Outfit]">2026 Cohort grid image (in What's At Stake section)</div>
            <div className="flex gap-2 items-start">
              <Input data-testid="cohort-image" value={cohortImg} onChange={(e) => setCohortImg(e.target.value)} className="bg-white border-emerald-200" />
              {cohortImg && <img src={cohortImg} alt="" className="h-12 w-20 object-cover rounded border border-emerald-100" />}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [authState, setAuthState] = useState(location.state?.user ? "ok" : "checking");
  const [user, setUser] = useState(location.state?.user || null);
  const [tab, setTab] = useState("submissions");

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      return; // AuthCallback handles
    }
    if (authState === "ok") return;
    (async () => {
      try {
        const r = await api.get("/auth/me");
        setUser(r.data);
        setAuthState(r.data.is_admin ? "ok" : "not_admin");
      } catch {
        setAuthState("login");
      }
    })();
  }, [authState]);

  const logout = async () => {
    await api.post("/auth/logout");
    setSessionToken(null);
    setAuthState("login");
    setUser(null);
  };

  if (authState === "checking") return <div className="min-h-screen flex items-center justify-center bg-[#FFFAF0] font-[Outfit]">Loading…</div>;
  if (authState === "login") return <LoginScreen />;
  if (authState === "not_admin") return (
    <div className="min-h-screen flex items-center justify-center bg-[#FFFAF0] p-6">
      <div className="max-w-md text-center">
        <h1 className="font-[Cormorant_Garamond] text-3xl">Access denied</h1>
        <p className="text-slate-600 font-[Outfit] mt-3">Your account ({user?.email}) is not authorized to access this dashboard.</p>
        <Button onClick={logout} className="mt-4 rounded-full bg-emerald-800 hover:bg-emerald-900 text-white">Sign out</Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FFFAF0] text-emerald-950">
      <nav className="bg-white border-b border-emerald-100 sticky top-0 z-30">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <button onClick={() => navigate("/")} data-testid="admin-home" className="font-[Cormorant_Garamond] text-xl">Business Showers · Admin</button>
            <div className="flex gap-1">
              {[["submissions", "Submissions"], ["contacts", "Contacts"], ["content", "Content"], ["event-hub", "Event Hub"]].map(([k, l]) => (
                <button
                  key={k}
                  data-testid={`tab-${k}`}
                  onClick={() => setTab(k)}
                  className={`text-sm font-[Outfit] px-3 py-1.5 rounded-full ${tab === k ? "bg-emerald-800 text-white" : "text-emerald-900 hover:bg-emerald-50"}`}
                >{l}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm font-[Outfit]">
            <span className="text-emerald-800">{user?.email}</span>
            <Button data-testid="logout-btn" onClick={logout} size="sm" variant="outline" className="rounded-full border-emerald-300 text-emerald-800">Sign out</Button>
          </div>
        </div>
      </nav>
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-8">
        {tab === "submissions" && <SubmissionsTab />}
        {tab === "contacts" && <ContactsTab />}
        {tab === "content" && <ContentTab />}
        {tab === "event-hub" && <EventHubAdminTab />}
      </div>
    </div>
  );
}
